import { FocusConfig } from "../agent-config-options";

const DEFAULT_FOCUS_FILE_PATH = ".claude/focus.json";
const DEFAULT_SCHEMA_VERSION = 1;

/**
 * Render the markdown subsection appended to the
 * `issue-label-conventions` rule when `AgentConfigOptions.focus` is
 * supplied. Returns an empty string when `focus` is undefined so
 * callers can unconditionally concatenate the result.
 *
 * The section documents three things for agents:
 *
 * 1. How to read `focus.json` at triage time
 * 2. How focus weight interacts with the `priority:*` taxonomy
 * 3. The agent-driven expansion contract (what agents may append)
 *
 * Configulator ships this rule content plus a JSON Schema that
 * validates the `focus.json` file; the file itself is authored and
 * curated in the consuming repo.
 */
export function renderFocusSection(focus: FocusConfig | undefined): string {
  if (!focus) {
    return "";
  }

  const focusFilePath = focus.focusFilePath ?? DEFAULT_FOCUS_FILE_PATH;
  const schemaVersion = focus.schemaVersion ?? DEFAULT_SCHEMA_VERSION;

  const lines: Array<string> = [
    "## Focus scoring",
    "",
    "This project runs a **focus-scoring engine** alongside the generic",
    "`priority:*` inference above. Focus scoring boosts the priority of",
    "issues that match the project's currently-declared focus areas —",
    "named customers, segments, organizations, software products,",
    "regulations, or people the project cares about right now.",
    "",
    "### Reading `focus.json`",
    "",
    `The focus file lives at \`${focusFilePath}\` and conforms to schema`,
    `version \`${schemaVersion}\`. Each entry declares a match predicate`,
    "(any combination of `labels`, `titleKeywords`, `bodyKeywords`) and",
    "a positive integer `weight`. At triage time:",
    "",
    `1. Read \`${focusFilePath}\`. If the file is missing or empty,`,
    "   skip focus scoring entirely — the generic priority heuristics",
    "   apply unchanged.",
    "2. For each focus area, test its match predicate against the",
    "   issue's labels, title, and body. A match on **any** sub-field",
    "   (labels / title keywords / body keywords) fires the area and",
    "   contributes its `weight` to the issue's focus score.",
    "3. Keyword matching is case-insensitive substring matching —",
    '   `titleKeywords: ["Acme"]` matches an issue titled',
    "   `feat: onboard acme corp`.",
    "4. Sum all matched weights to produce the issue's **focus score**.",
    "",
  ];

  lines.push("### How focus score interacts with `priority:*`");
  lines.push("");

  if (focus.thresholds) {
    const { high, medium } = focus.thresholds;
    lines.push(`A focus score of \`${high}\` or more boosts the`);
    const mediumBand =
      high - medium === 1
        ? `A focus score of \`${medium}\` keeps`
        : `A focus score of \`${medium}\` up to \`${high - 1}\` keeps`;
    lines.push(
      "issue to `priority:high`.",
      `${mediumBand} the issue at \`priority:medium\`. Scores below`,
      `\`${medium}\` do **not** alter the priority that`,
      "the generic heuristics would otherwise infer.",
    );
  } else {
    lines.push(
      "This project has not declared explicit focus-score thresholds.",
      "Treat any non-zero focus score as a soft signal to lift one",
      "priority tier (never above `priority:high`) unless the generic",
      "heuristics already pin a higher tier.",
    );
  }
  lines.push("");
  lines.push(
    "Focus scoring **never lowers** an issue's priority — the generic",
    "heuristics set the floor, and focus weight can only lift it.",
    "`priority:critical` is never set by focus scoring alone; the",
    "generic heuristics own that tier.",
  );
  lines.push("");

  lines.push("### Agent-driven expansion");
  lines.push("");

  if (focus.agentExpansionRules) {
    const rules = focus.agentExpansionRules;
    const allowedList = rules.allowedTypes.map((t) => `\`${t}\``).join(", ");
    const forbiddenList = rules.forbiddenTypes
      .map((t) => `\`${t}\``)
      .join(", ");

    lines.push(
      `Agents may **append** new focus areas to \`${focusFilePath}\``,
      "when they discover a relevant entity during work, subject to",
      "these guard-rails:",
    );
    lines.push("");
    lines.push(
      `- **Max weight.** Agent-appended entries must set \`weight\` to`,
      `  \`${rules.maxWeight}\` or less. Higher weights require a human.`,
    );
    lines.push(
      "- **Allowed types.** Agents may only introduce entries whose",
      `  \`type\` is one of: ${allowedList || "(none)"}.`,
    );
    if (rules.forbiddenTypes.length > 0) {
      lines.push(
        "- **Forbidden types.** Even inside `allowedTypes`, agents must",
        `  never introduce entries whose \`type\` is one of: ${forbiddenList}.`,
      );
    }
    if (rules.maxKeywords !== undefined) {
      lines.push(
        "- **Max keywords per match field.** Agent-appended entries",
        `  may list at most \`${rules.maxKeywords}\` items in each of`,
        "  `match.labels`, `match.titleKeywords`, and `match.bodyKeywords`.",
      );
    }
    lines.push(
      '- **Provenance.** Agent-appended entries must set `source: "agent"`',
      '  and `discoveredIn: "#<issue>"` referencing the issue that',
      "  surfaced the focus area.",
    );
    lines.push(
      "- **Append-only.** Never modify or remove existing entries.",
      "  Human curators own edits and deletions.",
    );
  } else {
    lines.push(
      `Agents must **not** append to \`${focusFilePath}\`. This project`,
      "has not declared agent-expansion rules — all focus-area curation",
      "is performed by humans. If you discover a relevant entity that",
      "is not covered by an existing focus area, raise it in the issue",
      "or PR discussion instead of editing the focus file.",
    );
  }

  return lines.join("\n");
}
